import { useEffect, useState } from 'react';
import { SW_UPDATE_AVAILABLE_EVENT, applyServiceWorkerUpdate } from '@/lib/service-worker';
import { useLockContext } from '@/contexts/LockContext';
import { useServerOnline } from '@/hooks/useServerOnline';
import { useBanner } from '@/hooks/useBanner';
import { updateMessage } from './messages';

/**
 * Posts "a new version is ready" once the service worker has one waiting, and
 * applies it silently on the lock screen instead.
 */
export function useUpdateBanner(): void {
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [reloading, setReloading] = useState(false);
  const { isLocked, unlockInProgress } = useLockContext();

  useEffect(() => {
    const handler = () => setPendingUpdate(true);
    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, handler);
    return () => window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, handler);
  }, []);

  // The silent apply below only runs while the server is reachable. Swapping
  // service workers offline is safe in itself — the new precache is complete
  // before the worker can activate — but a locked device in a client's home
  // gains nothing from it, and an unprompted reload there is disruptive. The
  // update stays pending and is applied when connectivity returns. The manual
  // Reload action is deliberately not gated: the user asked for it, and the
  // swap is safe. Like the action, the silent apply has no recovery path for
  // `reloading`: activation needs no network, so the `controlling` reload
  // follows even if connectivity drops right after the apply.
  const online = useServerOnline();

  // Apply silently on the lock screen when no unlock is in flight: the user
  // sees a brief reload and lands on the fresh lock screen, no message needed.
  // While unlocking we defer — a reload mid-WebAuthn kills the ceremony.
  // Once unlock resolves the deps change and this effect re-evaluates: a
  // failed unlock leaves us locked + idle (apply silently); a successful
  // unlock flips isLocked false and the message is posted below.
  useEffect(() => {
    if (pendingUpdate && isLocked && !unlockInProgress && !reloading && online) {
      setReloading(true);
      applyServiceWorkerUpdate();
    }
  }, [pendingUpdate, isLocked, unlockInProgress, reloading, online]);

  const reload = () => {
    setReloading(true);
    // The 'controlling' event in service-worker.ts triggers
    // window.location.reload() once the new SW takes over — that's why there
    // is no recovery path for `reloading`. We rely on registerServiceWorker()
    // running before any message is shown (it does, via index.tsx); if that
    // ordering ever breaks, the action stays at "Reloading…" indefinitely.
    applyServiceWorkerUpdate();
  };

  // Not while locked: either the silent apply just started or an unlock is in flight.
  useBanner(pendingUpdate && !isLocked ? updateMessage(reloading, reload) : null);
}
